"use client";
import { FormInput } from "../forms/form-input";
import { Form } from "../ui/form";
import { Button } from "../ui/button";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import Link from "next/link";
import { useLookup, useSignIn } from "@padi/lib/hooks/use-auth";
import { MFAVerifyForm } from "./mfa-verify-form";
import { GoogleAuthButton } from "./google-auth-button";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@padi/ui/dialog";

const formSchema = z.object({
  email: z.email({ message: "Invalid email address" }),
  password: z.string().optional(),
});

type SigninFormType = z.infer<typeof formSchema>;

export function SignInForm({ platform }: { platform?: string }) {
  const [mfaToken, setMfaToken] = useState<string | null>(null);

  const { mutate: signIn, isPending } = useSignIn({ platform });
  const { lookupUser, isRegistered, loading, resetLookup } =
    useLookup(platform);

  const isLoading = loading || isPending;

  const form = useForm<SigninFormType>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: "", password: "" },
  });

  const handleEditEmail = () => {
    resetLookup();
    form.setValue("password", "");
  };

  async function onSubmit(data: SigninFormType) {
    if (isRegistered && !data.password) {
      form.setError("password", { message: "Password is required" });
      return;
    }
    if (isRegistered) {
      signIn(data, {
        onSuccess: (res) => {
          if (res.mfaRequired && res.mfaToken) {
            setMfaToken(res.mfaToken);
          }
        },
      });
    } else {
      lookupUser(data.email);
    }
  }

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          {platform !== "admin" && (
            <>
              <GoogleAuthButton platform={platform || "buyer"} />
              <div className="relative flex items-center">
                <div className="flex-grow border-t border-gray-200"></div>
                <span className="flex-shrink mx-4 text-gray-400 text-xs font-semibold">
                  OR
                </span>
                <div className="flex-grow border-t border-gray-200"></div>
              </div>
            </>
          )}
          <div className="relative">
            <FormInput
              control={form.control}
              name="email"
              label="Email address"
              placeholder="Enter email address"
              disabled={isRegistered}
              className="placeholder:text-[#596B71] placeholder:text-[10px]"
            />
            {isRegistered && (
              <button
                type="button"
                onClick={handleEditEmail}
                className="absolute right-0 top-0 text-secondary text-xs font-bold hover:underline h-6 flex items-center"
              >
                Change
              </button>
            )}
          </div>

          {isRegistered && (
            <>
              <FormInput
                control={form.control}
                name="password"
                label="Password"
                placeholder="Enter password"
                className="placeholder:text-[#596B71] placeholder:text-[10px]"
                type="password"
              />
              <div className="text-left pb-4 text-secondary font-bold text-xs">
                <Link href="/forgot-password">Forgot Password?</Link>
              </div>
            </>
          )}

          <Button
            type="submit"
            variant="secondary"
            className="w-full text-[#FFFFFFF5]"
            disabled={isLoading}
            loading={isLoading}
          >
            {isRegistered ? "Sign In" : "Continue"}
          </Button>
        </form>
      </Form>

      <Dialog open={!!mfaToken} onOpenChange={() => setMfaToken(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader className="sr-only">
            <DialogTitle>Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              Verify your identity to complete sign in.
            </DialogDescription>
          </DialogHeader>
          {mfaToken && (
            <MFAVerifyForm
              mfaToken={mfaToken}
              onBack={() => setMfaToken(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
